import React, {useContext, useEffect, useState, useCallback} from "react";
import {useNavigate} from "react-router-dom";
import StyleContext from "../../contexts/StyleContext";
import {useAuth} from "../../contexts/AuthContext";
import {
  adminListBlogs,
  adminCreateBlog,
  adminUpdateBlog,
  adminDeleteBlog,
  fetchBlogs
} from "../../services/api";
import {timeAgo} from "../../utils/timeAgo";
import PostEditor from "./PostEditor";
import SettingsPanel from "./SettingsPanel";
import AdminInbox from "./AdminInbox";
import ResumeEditor from "./ResumeEditor";
import Loader from "../../components/loader/Loader";
import "../blog/Blog.scss";
import "../../styles/_form.scss";
import "../../styles/_admin-overrides.scss";

const emptyForm = {title: "", excerpt: "", content: "", cover_image: ""};

const AdminDashboard = () => {
  const {isDark} = useContext(StyleContext);
  const {isAuthenticated, isAdmin, token, logout} = useAuth();
  const nav = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("posts");          // posts | inbox | resume | settings
  const [mode, setMode] = useState("list");          // list | create | edit
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) nav("/admin/login");
  }, [isAuthenticated, isAdmin, nav]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      try {
        const data = await adminListBlogs(token);
        setPosts(data.blogs || []);
      } catch (e) {
        const data = await fetchBlogs({page: 1, perPage: 100});
        setPosts(data.blogs || []);
        setError("Admin API unreachable — showing public list (read-only).");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated && isAdmin && tab === "posts") load();
  }, [isAuthenticated, isAdmin, load, tab]);

  const startCreate = () => {
    setForm(emptyForm);
    setEditId(null);
    setMode("create");
    setTab("posts");
  };
  const startEdit = post => {
    setForm({
      title: post.title || "",
      excerpt: post.excerpt || "",
      content: post.content || "",
      cover_image: post.cover_image || "",
    });
    setEditId(post.id);
    setMode("edit");
    setTab("posts");
  };

  const savePost = async data => {
    setBusy(true);
    setError("");
    try {
      if (mode === "create") {
        await adminCreateBlog(data, token);
      } else {
        await adminUpdateBlog(editId, data, token);
      }
      setMode("list");
      setForm(emptyForm);
      setEditId(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async post => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await adminDeleteBlog(post.id, token);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  if (!isAuthenticated || !isAdmin) return null;

  // ---------- Editor (full-bleed) ----------
  if (mode === "create" || mode === "edit") {
    return (
      <PostEditor
        initial={form}
        mode={mode}
        busy={busy}
        error={error}
        onSave={savePost}
        onCancel={() => { setMode("list"); setError(""); }}
      />
    );
  }

  // ---------- Dashboard shell ----------
  return (
    <main className={`admin ${isDark ? "dark" : ""}`}>
      <h1>Admin Dashboard</h1>
      <p className="admin__lead">Manage your blog posts and site settings.</p>

      <div className="admin__tabs">
        <button
          className={tab === "posts" ? "active" : ""}
          onClick={() => setTab("posts")}
        >
          Posts
        </button>
        <button
          className={tab === "inbox" ? "active" : ""}
          onClick={() => setTab("inbox")}
        >
          Inbox
        </button>
        <button
          className={tab === "resume" ? "active" : ""}
          onClick={() => setTab("resume")}
        >
          Resume
        </button>
        <button
          className={tab === "settings" ? "active" : ""}
          onClick={() => setTab("settings")}
        >
          Settings
        </button>
        <span style={{flex: 1}} />
        <button className="secondary" onClick={() => { logout(); nav("/"); }}>
          Logout
        </button>
      </div>

      {error && tab === "posts" && <p className="modal__error">{error}</p>}

      {tab === "posts" && (
        <>
          <div className="admin__bar">
            <button onClick={startCreate}>+ New Post</button>
          </div>

          {loading ? <Loader inline label="Loading posts" /> : (
            <div className="admin__table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Created</th>
                    <th>Views</th>
                    <th>Likes</th>
                    <th style={{textAlign: "right"}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map(p => (
                    <tr key={p.id}>
                      <td className="admin__title-cell">{p.title}</td>
                      <td>{timeAgo(p.created_at)}</td>
                      <td>{p.views || 0}</td>
                      <td>{p.likes || 0}</td>
                      <td className="admin__actions">
                        <button onClick={() => startEdit(p)}>Edit</button>
                        <button className="danger" onClick={() => remove(p)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                  {posts.length === 0 && (
                    <tr><td colSpan="5" className="admin__empty">No posts yet — click “+ New Post” to publish one.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "inbox"    && <AdminInbox />}
      {tab === "resume"   && <ResumeEditor />}
      {tab === "settings" && <SettingsPanel />}
    </main>
  );
};

export default AdminDashboard;
